import React from 'react';
import type { PictureSelection } from './VerbaScholaQuizRevisionPreview';

/** Keep an explicit teacher choice attached to its proposal when a preview closes or remounts. */
export function useProposalPictureSelections(userId: number, lessonId: number) {
    const [saved, setSaved] = React.useState<Record<string, Record<string, PictureSelection>>>({});
    // An image request may complete in the same tick as a choice in another
    // gallery. Keep the latest choice synchronously available to both handlers.
    const savedRef = React.useRef(saved);
    const keyFor = React.useCallback((generationId: number) =>
        `verbaschola.picture-selection.${userId}.${lessonId}.${generationId}`, [userId, lessonId]);
    const read = React.useCallback((generationId: number): Record<string, PictureSelection> | null => {
        try {
            const raw = sessionStorage.getItem(keyFor(generationId));
            if (raw === null) return null;
            const value: unknown = JSON.parse(raw);
            if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
            return Object.fromEntries(Object.entries(value).filter(([original, selection]) =>
                typeof original === 'string' && selection !== null && typeof selection === 'object'
                && typeof (selection as PictureSelection).url === 'string'
                && typeof (selection as PictureSelection).altText === 'string')) as Record<string, PictureSelection>;
        } catch { return null; }
    }, [keyFor]);
    const selectionsFor = (generationId: number) => savedRef.current[keyFor(generationId)] ?? read(generationId) ?? {};
    const select = (generationId: number, original: string, selection: PictureSelection) => {
        const key = keyFor(generationId);
        const next = { ...(savedRef.current[key] ?? read(generationId) ?? {}), [original]: selection };
        savedRef.current = { ...savedRef.current, [key]: next };
        try { sessionStorage.setItem(key, JSON.stringify(next)); } catch { /* browser storage is optional */ }
        setSaved(savedRef.current);
    };
    const clear = (generationId: number) => {
        const key = keyFor(generationId);
        try { sessionStorage.removeItem(key); } catch { /* browser storage is optional */ }
        const next = { ...savedRef.current };
        delete next[key];
        savedRef.current = next;
        setSaved(next);
    };
    return { selectionsFor, select, clear };
}
